import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const PRIVACY_CONTENT = <div className="space-y-4 text-brown-light" data-id="element-18">
    <h3 className="font-serif text-xl text-brown mb-2" data-id="element-19">Privacy Policy</h3>
    <p data-id="element-20">Last updated: {new Date().toLocaleDateString()}</p>
    <p data-id="element-21">
      <strong data-id="element-22">1. Information We Collect</strong>
      <br data-id="element-23" />
      We only collect information necessary to process your purchase and deliver
      your digital product. This includes your email address and payment
      information (processed securely by our payment provider).
    </p>
    <p data-id="element-24">
      <strong data-id="element-25">2. How We Use Your Information</strong>
      <br data-id="element-26" />
      We use your email to send you the product download link and occasional
      updates about the product. We do not sell your data to third parties.
    </p>
    <p data-id="element-27">
      <strong data-id="element-28">3. Cookies</strong>
      <br data-id="element-29" />
      We use essential cookies to ensure the website functions properly and to
      facilitate the purchase process.
    </p>
    <p data-id="element-30">
      <strong data-id="element-31">4. Contact</strong>
      <br data-id="element-32" />
      If you have questions about your privacy, please contact the support email
      provided in your purchase receipt.
    </p>
  </div>;
const TERMS_CONTENT = <div className="space-y-4 text-brown-light" data-id="element-33">
    <h3 className="font-serif text-xl text-brown mb-2" data-id="element-34">Terms of Service</h3>
    <p data-id="element-35">Last updated: {new Date().toLocaleDateString()}</p>
    <p data-id="element-36">
      <strong data-id="element-37">1. Digital Product Policy</strong>
      <br data-id="element-38" />
      Due to the nature of digital products, all sales are final once the
      download link has been accessed. However, if you have technical issues,
      please contact support.
    </p>
    <p data-id="element-39">
      <strong data-id="element-40">2. License</strong>
      <br data-id="element-41" />
      Purchase grants you a single personal license to use this product. You may
      not redistribute, resell, or share the files with others.
    </p>
    <p data-id="element-42">
      <strong data-id="element-43">3. Disclaimer</strong>
      <br data-id="element-44" />
      This product is for educational purposes only. It is not medical advice.
      Always consult with your pediatrician regarding your baby's health and
      sleep habits.
    </p>
    <p data-id="element-45">
      <strong data-id="element-46">4. Limitation of Liability</strong>
      <br data-id="element-47" />
      We are not liable for any direct, indirect, incidental, or consequential
      damages arising from the use of this product.
    </p>
  </div>;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  // Lock body scroll when modal is open
  useEffect(() => {
    if (activeModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [activeModal]);
  return <>
      <footer className="py-12 px-6 bg-cream border-t border-brown/5 text-center" data-id="element-48">
        <div className="max-w-4xl mx-auto flex flex-col items-center gap-6" data-id="element-49">
          <p className="text-brown-light/60 text-sm" data-id="element-50">
            © {new Date().getFullYear()} The Nap-Trapped Reset. All rights
            reserved.
          </p>
          <div className="flex gap-6 text-sm text-brown-light/80" data-id="element-51">
            <button onClick={() => setActiveModal('privacy')} className="hover:text-brown hover:underline transition-colors" data-id="element-52">
              Privacy Policy
            </button>
            <button onClick={() => setActiveModal('terms')} className="hover:text-brown hover:underline transition-colors" data-id="element-53">
              Terms of Service
            </button>
          </div>
        </div>
      </footer>

      <AnimatePresence data-id="element-54">
        {activeModal && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setActiveModal(null)} className="fixed inset-0 bg-brown/20 backdrop-blur-sm z-50" data-id="element-55" />

            {/* Modal */}
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-56">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} className="bg-cream w-full max-w-lg max-h-[80vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col pointer-events-auto border border-brown/10" data-id="element-57">
                {/* Header */}
                <div className="p-4 border-b border-brown/5 flex justify-end" data-id="element-58">
                  <button onClick={() => setActiveModal(null)} className="p-2 hover:bg-brown/5 rounded-full transition-colors text-brown-light hover:text-brown" data-id="element-59">
                    <X className="w-5 h-5" data-id="element-60" />
                  </button>
                </div>

                {/* Content */}
                <div className="p-6 md:p-8 overflow-y-auto" data-id="element-61">
                  {activeModal === 'privacy' ? PRIVACY_CONTENT : TERMS_CONTENT}
                </div>
              </motion.div>
            </div>
          </>}
      </AnimatePresence>
    </>;
}